//@ts-nocheck
import useFetchData from '../../../hooks/useFetchData';
import NoTripsFound from '../NoTripsFound/NoTripsFound';
import TripTableSkeleton from '../../Skeleton/TripTableSkeleton/TripTableSkeleton';
import {
  XGInputSearch,
  XGCheckBox,
  XGAutocomplete,
  XGDateRangePicker
} from 'xg-cl';
import TripsList from '../TripsList/TripsList';
import TripsCardList from '../TripsCardList/TripsCardList';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef } from 'react';
import { isAfter } from 'date-fns';
import {
  isValidDate,
  formatDateForAPI,
  parseDateFromString
} from '../../../utils/dateUtility';
import { buildTripsQueryParams } from '../utils/buildTripsQueryParams';
import { format as formatDate, utcToZonedTime } from 'date-fns-tz';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import './TripsPanel.css';

// Default filter state
const defaultFilters = {
  search: '',
  viewTripsMode: 'active', // 'active' or 'all'
  statuses: [],
  destination: null,
  createdBy: null,
  tripDates: [null, null],
  lastModified: [null, null],
  sortModel: [{ field: 'lastModified', sort: 'desc' }]
};

const MIN_SEARCH_CHARS = 2; // recommended 2–3
const SEARCH_DEBOUNCE_MS = 400; // recommended 250–400ms

const TripsPanel = ({ destinations, tripStatuses, createdByOptions }) => {
  const { tripCreatorFilter, enableTripCards, enableDisplayStatuses } =
    useFlags();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['trips']);
  const [gridRows, setGridRows] = useState([]);
  const [forceReload, setForceReload] = useState(0);
  const [dateRangeError, setDateRangeError] = useState('');
  const [lastModifiedError, setLastModifiedError] = useState('');
  const tenant = useSelector((state) => state.tenant);
  const user = useSelector((state) => state.user);
  const storageKey = `tripsPanel_${tenant?.tenantid}_${user?.userId}`;

  const inactiveStatusIds = tripStatuses
    .filter((status) => {
      const statusCode = status.tripstatuscode?.toUpperCase();
      if (!enableDisplayStatuses) {
        return statusCode === 'CANCELLED' || statusCode === 'ARCHIVED';
      }
      return (
        statusCode === 'COMPLETE' ||
        statusCode === 'CANCELLED' ||
        statusCode === 'ARCHIVED'
      );
    })
    .map((status) => String(status.tripstatusid));

  const ITEMS_PER_PAGE = enableTripCards ? 15 : 10;

  // Helper functions for sessionStorage
  const loadFiltersFromStorage = () => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);

        // Timezone-independent date conversion
        if (parsed.tripDates && Array.isArray(parsed.tripDates)) {
          parsed.tripDates = [
            parsed.tripDates[0]
              ? parseDateFromString(parsed.tripDates[0])
              : null,
            parsed.tripDates[1]
              ? parseDateFromString(parsed.tripDates[1])
              : null
          ];
        }
        // Timezone-independent date conversion
        if (parsed.lastModified && Array.isArray(parsed.lastModified)) {
          parsed.lastModified = [
            parsed.lastModified[0]
              ? parseDateFromString(parsed.lastModified[0])
              : null,
            parsed.lastModified[1]
              ? parseDateFromString(parsed.lastModified[1])
              : null
          ];
        }

        // Merge with defaults to ensure all properties exist
        return { ...defaultFilters, ...parsed };
      }
    } catch (error) {
      console.warn('Error loading filters from sessionStorage:', error);
    }
    return defaultFilters;
  };

  const saveFiltersToStorage = useCallback(
    (filters) => {
      try {
        // Store dates as YYYY-MM-DD strings
        const filtersToSave = {
          ...filters,
          tripDates: [
            filters.tripDates[0]
              ? formatDateForAPI(filters.tripDates[0])
              : null,
            filters.tripDates[1] ? formatDateForAPI(filters.tripDates[1]) : null
          ],
          lastModified: [
            filters.lastModified[0]
              ? formatDateForAPI(filters.lastModified[0])
              : null,
            filters.lastModified[1]
              ? formatDateForAPI(filters.lastModified[1])
              : null
          ]
        };

        sessionStorage.setItem(storageKey, JSON.stringify(filtersToSave));
      } catch (error) {
        console.warn('Error saving filters to sessionStorage:', error);
      }
    },
    [storageKey]
  );

  // Load initial filters from URL params (for backward compatibility) or sessionStorage
  const getInitialFilters = () => {
    let filters = { ...defaultFilters };

    // Check URL first for backward compatibility
    const urlInput = searchParams.get('input');
    if (urlInput) filters.search = urlInput;

    const urlStatusId = searchParams.get('statusId');
    if (urlStatusId) filters.statuses = urlStatusId.split(';');

    const urlCityId = searchParams.get('cityId');
    const urlCountryId = searchParams.get('countryId');
    if (urlCityId || urlCountryId) {
      const filtered = destinations.filter(
        (dest) =>
          (urlCityId && dest.cityId === parseInt(urlCityId)) ||
          (urlCountryId && dest.countryId === parseInt(urlCountryId))
      );
      if (filtered.length > 0) filters.destination = filtered[0];
    }

    const urlCreatedBy = searchParams.get('createdByUserId');
    if (urlCreatedBy) {
      const filtered = createdByOptions.filter(
        (user) => user.staffid === parseInt(urlCreatedBy)
      );
      if (filtered.length > 0) filters.createdBy = filtered[0];
    }

    // Timezone-independent date parsing
    const urlStartDate = searchParams.get('startDate');
    const urlTripDates = searchParams.get('tripDates');
    if (urlStartDate) {
      const startDate = parseDateFromString(urlStartDate);
      filters.tripDates = [startDate, startDate];
    } else if (urlTripDates) {
      const dates = urlTripDates.split(';');
      filters.tripDates = [
        dates[0] ? parseDateFromString(dates[0]) : null,
        dates[1] ? parseDateFromString(dates[1]) : null
      ];
    }

    const urlLastModified = searchParams.get('lastModified');
    if (urlLastModified) {
      const dates = urlLastModified.split(';');
      filters.lastModified = [
        dates[0] ? parseDateFromString(dates[0]) : null,
        dates[1] ? parseDateFromString(dates[1]) : null
      ];
    }

    // If we have URL params, save them and return
    if (
      urlInput ||
      urlStatusId ||
      urlCityId ||
      urlCountryId ||
      urlCreatedBy ||
      urlStartDate ||
      urlTripDates ||
      urlLastModified
    ) {
      saveFiltersToStorage(filters);
      return filters;
    }

    // Otherwise load from sessionStorage
    return loadFiltersFromStorage();
  };

  // Single filters state
  const [filters, setFilters] = useState(getInitialFilters);

  // Local input state so typing is instant and not coupled to server calls
  const [searchInput, setSearchInput] = useState('');

  // Debounce timer + request sequencing to avoid stale results
  const searchDebounceRef = useRef(null);
  const activeRequestSeqRef = useRef(0);

  // Initialize current page from URL (pagination still uses URL)
  const [currentPage, setCurrentPage] = useState(() => {
    if (searchParams.get('skip')) {
      const skip = parseInt(searchParams.get('skip'));
      if (skip !== 0) {
        return skip / ITEMS_PER_PAGE + 1;
      }
    }
    return 1;
  });

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    saveFiltersToStorage(filters);
  }, [filters, saveFiltersToStorage]);

  // Clear URL params on mount (since we're using sessionStorage now)
  useEffect(() => {
    const paramsToRemove = [
      'input',
      'statusId',
      'statusCode',
      'cityId',
      'countryId',
      'createdByUserId',
      'startDate',
      'tripDates',
      'lastModified'
    ];
    let shouldUpdate = false;

    paramsToRemove.forEach((param) => {
      if (searchParams.has(param)) {
        searchParams.delete(param);
        shouldUpdate = true;
      }
    });

    if (shouldUpdate) {
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generic filter update function
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
    resetToFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTripDateRangeChange = (selectedDates) => {
    const [startDate, endDate] = selectedDates;

    setDateRangeError('');

    if (!startDate && !endDate) {
      setFilters((prev) => ({
        ...prev,
        tripDates: [null, null]
      }));
      return;
    }

    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      return;
    }

    if (isAfter(startDate, endDate)) {
      setDateRangeError(t('trips:endDateCannotBeEarlier'));
      return;
    }

    setFilters((prev) => ({
      ...prev,
      tripDates: selectedDates,
      quickModified: ''
    }));

    if (!isAfter(startDate, endDate)) {
      resetToFirstPage();
    }
  };

  const handleLastModifiedDateChange = (selectedDates) => {
    const [startDate, endDate] = selectedDates;

    setLastModifiedError('');

    if (!startDate && !endDate) {
      setFilters((prev) => ({
        ...prev,
        lastModified: [null, null],
        quickModified: ''
      }));
      return;
    }

    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      return;
    }

    if (isAfter(startDate, endDate)) {
      setLastModifiedError(t('trips:endDateCannotBeEarlier'));

      return;
    }

    setFilters((prev) => ({
      ...prev,
      lastModified: selectedDates,
      quickModified: ''
    }));

    if (!isAfter(startDate, endDate)) {
      resetToFirstPage();
    }
  };

  const handleViewTripsModeChange = (mode) => {
    setFilters((prev) => ({
      ...prev,
      viewTripsMode: mode,
      // Clear individual status selections when switching modes
      statuses: []
    }));
    resetToFirstPage();
  };

  const handleStatusChange = (statusId, isChecked) => {
    const newStatuses = isChecked
      ? [...filters.statuses, statusId]
      : filters.statuses.filter((status) => status !== statusId);

    updateFilter('statuses', newStatuses);
  };

  // Build query string with filters
  const buildQueryString = () => {
    return buildTripsQueryParams({
      filters,
      tripStatuses,
      enableDisplayStatuses,
      take: searchParams.get('take'),
      skip: searchParams.get('skip')
    }).toString();
  };

  const {
    data: tripsData,
    isLoading: tripDataLoading,
    error: tripsDataError
  } = useFetchData(`/trips?${buildQueryString()}&refresh=${forceReload}`);

  const handlePaginationChange = (event, page) => {
    searchParams.delete('take');
    searchParams.delete('skip');
    searchParams.set('take', ITEMS_PER_PAGE);
    searchParams.set('skip', (page - 1) * ITEMS_PER_PAGE);
    setSearchParams(searchParams);
    setCurrentPage(page);
  };

  const resetToFirstPage = () => {
    searchParams.delete('skip');
    searchParams.set('skip', 0);
    setSearchParams(searchParams);
    setCurrentPage(1);
    setForceReload(new Date().getTime());
  };

  const handleSortChange = (newSortModel) => {
    updateFilter('sortModel', newSortModel);
  };

  const onRefresh = (page = 1) => {
    handlePaginationChange(null, page);
    setForceReload(new Date().getTime());
  };

  const areFiltersCleared = (filters) => {
    return (
      filters.search === '' &&
      filters.viewTripsMode === 'active' &&
      filters.statuses.length === 0 &&
      filters.destination === null &&
      filters.createdBy === null &&
      !filters.tripDates[0] &&
      !filters.tripDates[1] &&
      !filters.lastModified[0] &&
      !filters.lastModified[1]
    );
  };

  const handleClearAll = () => {
    // Early return if filters are already cleared
    if (areFiltersCleared(filters)) {
      return;
    }

    // Clear pagination from URL
    searchParams.delete('take');
    searchParams.delete('skip');
    searchParams.delete('statusCode');
    searchParams.delete('statusId');
    setSearchParams(searchParams);

    // Reset filters to default but preserve sorting
    setFilters((prev) => ({
      ...defaultFilters,
      sortModel: prev.sortModel // Keep current sorting
    }));
    setDateRangeError('');

    // Clear sessionStorage and save back the current sort model
    const currentSortModel = filters.sortModel;
    sessionStorage.removeItem(storageKey);

    // Save the filters with preserved sorting
    const filtersWithSort = {
      ...defaultFilters,
      sortModel: currentSortModel
    };
    saveFiltersToStorage(filtersWithSort);

    resetToFirstPage();
  };

  useEffect(() => {
    if (tripsData && tripsData.trips.length > 0) {
      const rows = [];
      tripsData?.trips?.map(
        ({
          tripid,
          tripname,
          city,
          country,
          startdate,
          modifieddatetime,
          tripstatusid,
          tripstatusdisplaycode,
          createdByUser,
          updatedByUser,
          passengersCount,
          capacity,
          tripTotal,
          amountReceivedTotal,
          paymentSchedules = []
        }) => {
          const cityName = city?.cityname || '';
          const countryName = country?.countryname || '';
          const destination =
            cityName && countryName
              ? `${cityName}, ${countryName}`
              : cityName || countryName || '-';

          const newStartDate = startdate
            ? startdate.split('T')[0].toString()
            : null;

          const trip = {
            id: tripid,
            title: tripname,
            destination: destination,
            lastModified: modifieddatetime
              ? formatDate(new Date(modifieddatetime), 'dd LLL yyyy')
              : '-',
            startDate: startdate
              ? formatDate(
                  utcToZonedTime(new Date(newStartDate), 'UTC'),
                  'dd MMM yyyy',
                  { timeZone: 'UTC' }
                )
              : '-',
            status:
              (enableDisplayStatuses ? tripstatusdisplaycode : null) ||
              tripStatuses.find(
                (tripstatus) => tripstatus.tripstatusid === tripstatusid
              )?.tripstatuscode ||
              '',
            createdByUser,
            updatedByUser,
            passengersCount: parseInt(passengersCount) || 0,
            maxPassengers: parseInt(capacity) || 0,
            revenue: Number(amountReceivedTotal || 0) || 0,
            maxRevenue: Number(tripTotal || 0) || 0,
            paymentSchedules
          };

          rows.push(trip);
          return trip;
        }
      );
      setGridRows(rows);
    } else if (tripsData && tripsData.trips.length === 0) {
      setGridRows([]);
    }
  }, [tripsData, tripStatuses, enableDisplayStatuses]);

  // This just syncs input when filters are loaded from storage / cleared.
  useEffect(() => {
    setSearchInput(filters?.search ?? '');
  }, [filters?.search]);

  // Apply search filter with debounce and threshold
  const applySearchFilter = useCallback(
    (rawValue) => {
      const value = (rawValue ?? '').trimStart(); // keep existing behavior, just avoid leading spaces spam

      // Threshold rule:
      // - If empty => clear search immediately
      // - If below threshold => do NOT query (but keep input text)
      if (value.length !== 0 && value.length < MIN_SEARCH_CHARS) {
        return;
      }

      updateFilter('search', value);

      // Reset to first page on new search
      setForceReload((n) => n + 1);
    },
    [updateFilter]
  );

  const onSearchChange = useCallback(
    (e) => {
      const nextValue = e.target.value;
      setSearchInput(nextValue);

      // Cancel previous debounce
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      // Each keystroke increments the "intent" sequence
      // used to ignore stale results.
      const mySeq = ++activeRequestSeqRef.current;

      searchDebounceRef.current = setTimeout(() => {
        // Only apply if this is still the latest keystroke
        if (mySeq !== activeRequestSeqRef.current) return;
        applySearchFilter(nextValue);
      }, SEARCH_DEBOUNCE_MS);
    },
    [applySearchFilter]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      // invalidate any pending work
      activeRequestSeqRef.current += 1;
    };
  }, []);

  // Optional: if user presses Enter or clicks the search icon, apply immediately.
  const applySearchImmediately = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    // invalidate pending debounced calls
    activeRequestSeqRef.current += 1;
    applySearchFilter(searchInput);
  }, [applySearchFilter, searchInput]);

  return (
    <Box data-testid='trips-component' className='trips-panel'>
      {!enableTripCards && (
        <Box
          style={{
            paddingLeft: '16px',
            paddingRight: '16px',
            marginTop: '8px',
            marginBottom: '8px',
            width: '450px'
          }}
        >
          <XGInputSearch
            className='trips-inputFilter'
            id='trips-filter-input'
            placeholder={t('searchByTitle')}
            name='search'
            type='text'
            onChange={onSearchChange}
            onClick={applySearchImmediately}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                applySearchImmediately();
              }
            }}
            value={searchInput}
          />
        </Box>
      )}
      <Box className='trips-panel__content'>
        <Box
          className='trip-filters'
          style={{
            minHeight: enableTripCards ? 'calc(100vh - 128px)' : 'auto'
          }}
        >
          <Box className='trip-filters__header'>
            <span className='trip-filters__header-text trip-filters__filter-by'>
              {t('filterBy')}
            </span>
            <span
              className='trip-filters__header-text trip-filters__clear-all'
              onClick={handleClearAll}
            >
              {t('clearAll')}
            </span>
          </Box>
          <Box className='trip-filters__body'>
            {/* View Trips Toggle */}
            <Box>
              <p className='trips-filters__filter-title'>{t('viewTrips')}</p>
              <Box className='trips-filters__view-trips-toggle'>
                <button
                  className={`trips-filters__toggle-button ${
                    filters.viewTripsMode === 'active'
                      ? 'trips-filters__toggle-button--active'
                      : ''
                  }`}
                  onClick={() => handleViewTripsModeChange('active')}
                >
                  {t('active')}
                </button>
                <button
                  className={`trips-filters__toggle-button ${
                    filters.viewTripsMode === 'all'
                      ? 'trips-filters__toggle-button--active'
                      : ''
                  }`}
                  onClick={() => handleViewTripsModeChange('all')}
                >
                  {t('all')}
                </button>
              </Box>
              <p className='trips-filters__trip-count'>
                {filters.viewTripsMode === 'active'
                  ? t('activeTrips', { count: tripsData?.count || 0 })
                  : t('totalTrips', { count: tripsData?.count || 0 })}
              </p>
            </Box>

            {/* Status Checkboxes */}
            <Box>
              <p className='trips-filters__filter-title'>{t('status')}</p>
              <Box className='trips-filters__checkbox-main-wrapper'>
                {tripStatuses.length !== 0 &&
                  tripStatuses.map((tripStatus) => {
                    const statusId = String(tripStatus.tripstatusid);
                    const isInactive = inactiveStatusIds.includes(statusId);
                    const isDisabled =
                      filters.viewTripsMode === 'active' && isInactive;

                    return (
                      <Box
                        key={tripStatus.tripstatusid}
                        className='trips-filters__checkbox-wrapper'
                      >
                        <XGCheckBox
                          id={`${tripStatus.tripstatusid}`}
                          value={tripStatus.tripstatusid}
                          size='small'
                          label={tripStatus.tripstatusname}
                          style={{ marginRight: '12px' }}
                          checked={filters.statuses.includes(statusId)}
                          disabled={isDisabled}
                          onChange={(e) => {
                            handleStatusChange(
                              e.target.value,
                              e.target.checked
                            );
                          }}
                        />
                      </Box>
                    );
                  })}
              </Box>
            </Box>
            <Box className='trips-filters__dates-wrapper'>
              <Box className='trips-filters__autocomplete-wrapper'>
                <p className='trips-filters__filter-title'>
                  {t('destination')}
                </p>

                <XGAutocomplete
                  placeholder={t('searchDestination')}
                  name='destination'
                  id='trip-destination-input'
                  options={destinations}
                  isOptionEqualToValue={(option, value) =>
                    option.title === value?.title
                  }
                  onChange={(event, newValue) => {
                    updateFilter('destination', newValue);
                  }}
                  value={filters.destination}
                  disableClearable={false}
                />
              </Box>
              {tripCreatorFilter && (
                <Box className='trips-filters__autocomplete-wrapper'>
                  <p className='trips-filters__filter-title'>
                    {t('tripCreatedByFilter')}
                  </p>

                  <XGAutocomplete
                    placeholder={t('searchUser')}
                    name='createdBy'
                    id='trip-createdby-input'
                    options={createdByOptions}
                    isOptionEqualToValue={(option, value) =>
                      option.userid === value?.userid
                    }
                    onChange={(event, newValue) => {
                      updateFilter('createdBy', newValue);
                    }}
                    value={filters.createdBy}
                    disableClearable={false}
                  />
                </Box>
              )}

              {/* Trip Dates Range Picker */}
              <Box className='trips-filters__tripDatePicker'>
                <p className='trips-filters__filter-title'>{t('tripDates')}</p>
                <Box className='trips-filters__range-picker-wrapper'>
                  <XGDateRangePicker
                    id='trips-filters-trip-dates-range'
                    format={`${t('common:dateFormat')}`}
                    onChange={handleTripDateRangeChange}
                    value={filters.tripDates}
                    error={dateRangeError !== ''}
                    slotProps={{
                      field: {
                        clearable: true,
                        placeholder: 'Start / End'
                      }
                    }}
                  />
                  {dateRangeError && (
                    <Box className='trips-filters__error-message'>
                      <span style={{ color: '#d32f2f', fontSize: '12px' }}>
                        {dateRangeError}
                      </span>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box>
                <p className='trips-filters__filter-title'>
                  {t('lastModified')}
                </p>
                <Box className='trips-filters__range-picker-wrapper'>
                  <XGDateRangePicker
                    id='trips-filters-lastmodified-date-range'
                    format={`${t('common:dateFormat')}`}
                    onChange={handleLastModifiedDateChange}
                    value={filters.lastModified}
                    error={lastModifiedError !== ''}
                    slotProps={{
                      field: {
                        clearable: true,
                        placeholder: 'Start / End'
                      }
                    }}
                  />
                  {lastModifiedError && (
                    <Box className='trips-filters__error-message'>
                      <span style={{ color: '#d32f2f', fontSize: '12px' }}>
                        {lastModifiedError}
                      </span>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          className={`trips-panel__list ${enableTripCards ? 'trips-panel__list--no-scroll ' : ''}`}
        >
          {tripDataLoading && !enableTripCards ? (
            <Box
              style={{
                width: '100%',
                height: '100%',
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start'
              }}
            >
              <TripTableSkeleton id='trips-table-skeleton' />
            </Box>
          ) : null}

          {enableTripCards ? (
            <>
              <TripsCardList
                initialTrips={gridRows}
                totalRecordsCount={tripsData?.count || 0}
                onSortChange={handleSortChange}
                sortModel={filters.sortModel}
                loading={tripDataLoading}
                tripStatuses={tripStatuses}
                enableDisplayStatuses={enableDisplayStatuses}
                filters={filters}
                onTripDeleted={onRefresh}
                onChangeSearchInput={onSearchChange}
                onClickSearchInput={applySearchImmediately}
                onKeyDownSearchInput={(event) => {
                  if (event.key === 'Enter') {
                    applySearchImmediately();
                  }
                }}
                searchInput={searchInput}
              />
            </>
          ) : (
            <>
              {!tripDataLoading && !tripsDataError && tripsData ? (
                tripsData.trips.length !== 0 ? (
                  <TripsList
                    trips={gridRows}
                    totalRecordsCount={tripsData?.count}
                    onPaginationChange={handlePaginationChange}
                    currentPage={currentPage}
                    onRefresh={onRefresh}
                    onSortChange={handleSortChange}
                    sortModel={filters?.sortModel}
                    loading={tripDataLoading}
                  />
                ) : (
                  <Box
                    style={{
                      width: '83%',
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <NoTripsFound />
                  </Box>
                )
              ) : null}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

TripsPanel.propTypes = {
  destinations: PropTypes.arrayOf(
    PropTypes.shape({
      countryId: PropTypes.number,
      cityId: PropTypes.number,
      title: PropTypes.string
    })
  ),
  createdByOptions: PropTypes.arrayOf(
    PropTypes.shape({
      staffid: PropTypes.number,
      userid: PropTypes.number,
      lastname: PropTypes.string,
      firstname: PropTypes.string,
      email: PropTypes.string
    })
  ),
  tripStatuses: PropTypes.arrayOf(
    PropTypes.shape({
      tripstatusid: PropTypes.number,
      description: PropTypes.string,
      tripstatuscode: PropTypes.string,
      tripstatusname: PropTypes.string
    })
  )
};

export default TripsPanel;
